/**
 * <UpcomingSessionsSection> — « À venir » sur l'accueil parent, « Mes prochains lives » sur
 * « Messages » (template).
 *
 * Les séances qui suivent la prochaine — deux au plus, coupées par le view-model — et « Voir plus
 * · Tous mes lives », dans UNE carte ordinaire. La prochaine séance n'y est pas : elle a sa propre section, en tête de
 * l'accueil, dans le cadre des temps forts (`<NextSessionSection>`). La liste traverse les jours
 * exprès — une famille qui a plusieurs lives par semaine veut voir les suivants, pas le reste de
 * la journée.
 *
 * Rien après la prochaine séance → la section entière disparaît, titre compris : l'état vide est
 * déjà dit par « Prochaine séance ». Sauf quand « Tous mes lives » a plus à montrer
 * (`hasMoreLives`) — une séance où l'absence est signalée n'est pas sur l'accueil : la carte ne
 * garde alors que « Voir plus », seul chemin vers cette séance et son « Annuler l'absence ».
 *
 * Toucher une ligne ouvre la feuille des actions de SA séance, comme sur « Tous mes lives ».
 *
 * **Pour l'élève (`voice="student"`), la même carte se lit seule** : aucune carte de la prochaine
 * séance ne la précède sur « Messages ». Elle garde donc son titre quand elle est vide
 * (« Aucune session planifiée »), ses lignes disent le jour comme on le dit (« Aujourd'hui ·
 * 18h00 ») et écrivent de quelle absence une séance est le rattrapage. Les séances montrées restent
 * l'affaire du view-model : celles du premier jour qui en a. Et « Planifier un rattrapage », que le
 * parent trouve dans « À faire », est son bouton sous la liste (`onPlanRecovery`).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { CardList, CardListLinkRow } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { List } from "../../molecules/List"
import { SessionRow } from "../../molecules/SessionRow"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { cappedCount } from "../../utils/cappedCount"
import { type Voice } from "../../utils/voice"

const TEXTS: Record<Voice, { title: string; seeMore: string; seeMoreHint?: string; empty?: string }> = {
    parent: { title: "À venir", seeMore: "Voir plus", seeMoreHint: "· Tous mes lives" },
    student: { title: "Mes prochains lives", seeMore: "Voir tous mes lives", empty: "Aucune session planifiée" },
}

export type UpcomingSessionsSectionProps = {
    /** Les lignes à montrer — les séances APRÈS la prochaine, triées et coupées par le view-model. */
    sessions: UILiveSession[]
    /** D'autres séances existent au-delà de celles montrées — pilote « Voir plus ». */
    hasMoreLives?: boolean
    /** « Voir plus · Tous mes lives ». */
    onSeeMoreLives?: () => void
    /** Ouvre les actions de la séance touchée. Absent → rien ne presse. */
    onSelectSession?: (session: UILiveSession) => void
    /** À qui la section parle — défaut `"parent"`. */
    voice?: Voice
    /**
     * « Planifier un rattrapage », sous la liste : l'élève a une absence à rattraper. ABSENT = pas
     * de bouton (le parent, lui, a sa ligne « À faire »).
     */
    onPlanRecovery?: () => void
    /** Les absences rattrapables dès maintenant : leur nombre s'écrit sur le bouton. Défaut 0, rien. */
    recoverableNowCount?: number
    testID?: string
}

/** La section a-t-elle quelque chose à montrer ? Sinon elle disparaît, et la page ne lui pose pas de colonne. */
export function hasUpcomingContent(sessions: UILiveSession[], hasMoreLives = false): boolean {
    return sessions.length > 0 || hasMoreLives
}

export function UpcomingSessionsSection(props: UpcomingSessionsSectionProps) {
    const {
        sessions,
        hasMoreLives = false,
        onSeeMoreLives,
        onSelectSession,
        voice = "parent",
        onPlanRecovery,
        recoverableNowCount = 0,
        testID,
    } = props
    const texts = TEXTS[voice]
    const hasContent = hasUpcomingContent(sessions, hasMoreLives)
    if (!hasContent && texts.empty == null) return null

    // La liste de l'élève se lit seule : ses lignes disent le jour, et de quoi elles sont le rattrapage.
    const standalone = voice === "student"

    return (
        <List
            title={texts.title}
            footer={
                onPlanRecovery != null ? (
                    <PlanRecoveryButton count={recoverableNowCount} onPress={onPlanRecovery} />
                ) : undefined
            }
            testID={testID}
        >
            {hasContent ? (
                <CardList>
                    {sessions.map((session) => (
                        <SessionRow
                            key={session.id}
                            session={session}
                            relativeDay={standalone}
                            showRecoveryInfo={standalone}
                            onPress={onSelectSession != null ? () => onSelectSession(session) : undefined}
                        />
                    ))}
                    {hasMoreLives && (
                        <CardListLinkRow label={texts.seeMore} hint={texts.seeMoreHint} onPress={onSeeMoreLives} />
                    )}
                </CardList>
            ) : (
                <EmptyState title={texts.empty ?? ""} />
            )}
        </List>
    )
}

/**
 * « Planifier un rattrapage » — sous cette liste, et en tête de « Tous mes lives » de l'élève
 * (`<LiveSessionsListPage onPlanRecovery>`).
 *
 * Le bouton garde sa couleur : c'est la pastille qui dit qu'un rattrapage peut se planifier dès
 * maintenant, et combien — une variante de bouton dit une action, jamais un état. Le nombre se
 * dit aussi au lecteur d'écran, dans le nom du bouton : la pastille ne se lit pas.
 */
export function PlanRecoveryButton({ count, onPress }: { count: number; onPress: () => void }) {
    return (
        <View style={styles.recovery}>
            <Button
                label="Planifier un rattrapage"
                iconName="redo"
                variant="outline"
                onPress={onPress}
                accessibilityLabel={
                    count > 0
                        ? `Planifier un rattrapage, ${count === 1 ? "1 possible" : `${count} possibles`}`
                        : undefined
                }
            />
            {count > 0 && (
                <View style={styles.recoveryCount} pointerEvents="none">
                    <Badge text={cappedCount(count)} variant="danger" />
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** À sa taille, à gauche : étiré sur la colonne d'un grand écran, il pèserait plus que la liste. */
    recovery: { alignSelf: "flex-start" },
    recoveryCount: {
        position: "absolute",
        top: -theme.spacing["2"],
        right: -theme.spacing["2"],
    },
}))
